import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import {
  Copy,
  Check,
  Facebook,
  Twitter,
  Mail,
  MessageCircle,
  Gift,
} from "lucide-react";

interface ReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ReferralModal = ({ open, onOpenChange }: ReferralModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState<{
    total_referrals: number;
    pending_referrals: number;
    completed_referrals: number;
    total_earned: number;
    available_credits: number;
  } | null>(null);

  useEffect(() => {
    if (open && user) {
      fetchReferralCode();
      fetchStats();
    }
  }, [open, user]);

  const fetchReferralCode = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // First try to get existing code
      const { data: profile } = await supabase
        .from("profiles")
        .select("referral_code")
        .eq("id", user.id)
        .single();

      if (profile?.referral_code) {
        setReferralCode(profile.referral_code);
      } else {
        // Generate new code
        const { data, error } = await supabase.rpc("generate_referral_code", {
          target_user_id: user.id,
        });
        if (error) throw error;
        setReferralCode(data);
      }
    } catch (error) {
      console.error("Error fetching referral code:", error);
      toast({
        title: "Error",
        description: "Failed to load referral code",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase.rpc("get_referral_stats", {
        target_user_id: user.id,
      });
      if (error) throw error;
      if (data && data.length > 0) {
        setStats(data[0]);
      }
    } catch (error) {
      console.error("Error fetching referral stats:", error);
    }
  };

  const referralLink = referralCode
    ? `${window.location.origin}/?ref=${referralCode}`
    : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Failed to copy",
        description: "Please copy the link manually",
        variant: "destructive",
      });
    }
  };

  const shareText = `Join AnySkill and start earning money with your skills! Use my referral link: ${referralLink}`;

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
    email: `mailto:?subject=${encodeURIComponent("Join AnySkill!")}&body=${encodeURIComponent(shareText)}`,
  };

  const handleShare = (platform: keyof typeof shareLinks) => {
    window.open(shareLinks[platform], "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Gift className="h-6 w-6 text-anyskill-purple" />
            Refer for $10 to spend on AnySkill
          </DialogTitle>
        </DialogHeader>

        {!user ? (
          <div className="py-6 text-center">
            <p className="text-muted-foreground mb-4">
              Sign in to access your personal referral link and start earning!
            </p>
            <Button asChild className="bg-anyskill-purple hover:bg-anyskill-purple/90">
              <Link to="/auth" onClick={() => onOpenChange(false)}>
                Sign In to Get Started
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* How it works */}
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold mb-2">How it works:</h4>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Share your unique referral link</li>
                <li>Your friend signs up using the link</li>
                <li>
                  <span className="font-medium text-foreground">
                    They post their first service
                  </span>
                </li>
                <li>You get $10 credit to spend on AnySkill!</li>
              </ol>
              <p className="text-xs text-muted-foreground mt-2 italic">
                Note: Your friend must actually post a service, not just create an account.
              </p>
            </div>

            {/* Referral Link */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Referral Link</label>
              <div className="flex gap-2">
                <Input
                  value={loading ? "Loading..." : referralLink}
                  readOnly
                  className="text-sm"
                />
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  size="icon"
                  disabled={loading || !referralCode}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Share via</label>
              <div className="grid grid-cols-4 gap-2">
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-blue-50 hover:border-blue-500 dark:hover:bg-blue-950"
                  onClick={() => handleShare("facebook")}
                  disabled={!referralCode}
                >
                  <Facebook className="h-5 w-5 text-blue-600" />
                  <span className="text-xs">Facebook</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-sky-50 hover:border-sky-500 dark:hover:bg-sky-950"
                  onClick={() => handleShare("twitter")}
                  disabled={!referralCode}
                >
                  <Twitter className="h-5 w-5 text-sky-500" />
                  <span className="text-xs">Twitter</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-green-50 hover:border-green-500 dark:hover:bg-green-950"
                  onClick={() => handleShare("whatsapp")}
                  disabled={!referralCode}
                >
                  <MessageCircle className="h-5 w-5 text-green-500" />
                  <span className="text-xs">WhatsApp</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-orange-50 hover:border-orange-500 dark:hover:bg-orange-950"
                  onClick={() => handleShare("email")}
                  disabled={!referralCode}
                >
                  <Mail className="h-5 w-5 text-orange-500" />
                  <span className="text-xs">Email</span>
                </Button>
              </div>
            </div>

            {/* Stats */}
            {stats && stats.total_referrals > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Your Referral Stats</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Referrals</p>
                    <p className="font-bold text-lg">{stats.total_referrals}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Completed</p>
                    <p className="font-bold text-lg text-green-600">
                      {stats.completed_referrals}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Pending</p>
                    <p className="font-bold text-lg text-yellow-600">
                      {stats.pending_referrals}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Available Credit</p>
                    <p className="font-bold text-lg text-anyskill-purple">
                      ${stats.available_credits}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReferralModal;
